import { FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { hideKeyboard, registerModalBackHandler } from '../lib/native';
import { OAUTH_REDIRECT_NATIVE, OAUTH_REDIRECT_WEB } from '../lib/platform';
import {
  Avatar,
  Button,
  ChatComposer,
  ChatHeader,
  ChatNotice,
  FlowProgressBar,
  FlowProgressCount,
  QUESTION_SHEET_MOTION_MS,
  IconButton,
  Input,
  MessageBubble,
  Sidebar,
  Spinner,
  Stepper,
  TripSummaryModal,
} from '../components/design-system';
import { useIsDarkTheme, useTheme } from '../hooks/useTheme';
import { useAuth } from '../hooks/useAuth';
import {
  TRY_CHAT_STORAGE_KEY,
  ensureTryChatConversation,
  extractTryChatPortrait,
  finishTryChatWithPortrait,
  incompleteTripMessage,
  lastIncompleteReason,
  clearTryChatComplete,
  isTryChatComplete,
  markTryChatComplete,
  readPendingTryChat,
} from '../lib/tryChatHandoff';
import type { FinishResult } from '../lib/tryChatHandoff';
import type { StructuredPortrait } from '../types';
import { hasSeenOnboarding } from '../lib/onboardingState';
import { isNative } from '../lib/platform';
import { OAuthButtons } from '../components/auth/OAuthButtons';
// TryIntroModal is deactivated — replaced by OnboardingCarousel below, which
// covers the same persona-video intro plus real auth entry points. Re-enable
// by restoring this import and swapping it back in at the "Intro modal" spot.
// import { TryIntroModal } from '../components/alma/TryIntroModal';
import { OnboardingCarousel } from '../components/onboarding/OnboardingCarousel';
import { TravelerTabBar } from '../components/layout/TravelerTabBar';
import { FaqModal } from '../components/FaqModal';
import { SupportModal } from '../components/SupportModal';
import { FeedbackPopover } from '../components/FeedbackPopover';
import type { Message } from '../types';
import { AlmaFlowSheet } from '../components/alma/AlmaFlowSheet';
import { useFeatureFlag } from '../lib/featureFlags';
import { useAlmaFlow } from '../lib/almaFlow/useAlmaFlow';
import { clearFlowDraft, readDraftAnswers, readFlowDraft } from '../lib/almaFlow/draft';
import { flowAnswers, rewindTarget } from '../lib/almaFlow/flow';
import type { FlowState, SheetView } from '../lib/almaFlow/flow';
import type { AlmaFlowAnswers } from '../lib/almaFlow/types';
import { COMPOSER_PLACEHOLDER_NAME, SCRIPTED_TYPING_MS } from '../lib/almaFlow/flowConfig';
import { trackAlma } from '../lib/almaFlow/analytics';
import { readSse } from '../lib/sseStream';
import { lightTap } from '../lib/haptics';
import { BUBBLE_PAUSE_MS, revealDurationMs, splitBubbles } from '../lib/almaFlow/bubbles';
import { walkingLabel } from '../lib/almaFlow/recap';
import { ALMA_QUESTION_MARKER } from '../../supabase/functions/_shared/almaPhase2Config.ts';
import { usePart2Budget } from '../lib/almaFlow/usePart2Budget';
import {
  ALMA_COMPLETE_MARKER as COMPLETE_MARKER,
  ALMA_LIMITS,
  stripAlmaMarkers as stripMarker,
} from '../../supabase/functions/_shared/almaGuards.ts';
import twoPeopleUrl from '../assets/illustrations/two-people.svg?url';
import toastUrl from '../assets/illustrations/toast.svg?url';
import barmanUrl from '../assets/illustrations/barman.svg?url';
import { scrollListToBottom } from '../lib/keyboard';
import { PasswordRules } from '../components/auth/PasswordRules';
import { friendlyPasswordError, passwordProblem } from '../lib/passwordPolicy';

interface PersonaInfo {
  name: string;
  avatar_url: string | null;
}

// Shared with lib/tryChatHandoff — that's what reads this key back after auth.
const STORAGE_KEY = TRY_CHAT_STORAGE_KEY;
// A fast/cached reply can resolve in well under a second — too quick for the
// typing dots to register as "Alma is typing" at all. Holds the reply
// bubble's content back (see stream()) for at least this long before
// revealing text, so the dots-to-text swap always has a beat to read.
const MIN_TYPING_VISIBLE_MS = 500;
// Mirrors MIN_TYPING_VISIBLE_MS above: a fast finalize would otherwise flash
// the "Mapping your taste" notice for a fraction of a second, too quick
// for its fade-in/hold/loop sequence to ever be seen.
const MIN_FINALIZING_VISIBLE_MS = 3500;
// After Alma's closing message has finished appearing, how long before the "I've got you" notice shows: time to read it.
const CLOSING_NOTICE_DELAY_MS = 3000;
// Going back in Part 1: how long the undone chat lines fade before they're removed.
const REWIND_FADE_MS = 150;
const REWIND_FADE_STYLE = { opacity: 0, transform: 'translateY(6px)', transition: `opacity ${REWIND_FADE_MS}ms ease-out, transform ${REWIND_FADE_MS}ms ease-out` };

/** Part 2 request extras: the Phase 1 answers, and where Part 2 starts in the transcript. */
interface Phase2Request {
  intake: AlmaFlowAnswers;
  from: number;
}

function firstName(fullName: string | null | undefined): string | null {
  const first = fullName?.trim().split(/\s+/)[0];
  return first || null;
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

function illustration(src: string, width: number, height: number) {
  return (
    <div
      style={{
        width, height, background: 'var(--text)',
        WebkitMask: `url(${src}) no-repeat center / contain`,
        mask: `url(${src}) no-repeat center / contain`,
      }}
    />
  );
}

function formatTime(timestamp: string): string {
  return new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

async function detectCountry(): Promise<string | null> {
  try {
    const res = await fetch('https://ipapi.co/json/', { signal: AbortSignal.timeout(3000) });
    if (!res.ok) return null;
    const data = await res.json();
    return data.country_name ?? null;
  } catch {
    return null;
  }
}

/**
 * TryChat — the public "chat with Alma" taste-mapping screen (/try).
 * Design source: chatwithalma.dc.html — journey stepper sidebar, Alma
 * header with gradient fade, grouped bubbles, typing dots,
 * finalizing/failed interstitials, pill composer.
 */
export function TryChat() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialMessage = searchParams.get('message');
  // `toggle` is a stateless action — safe to call from this instance. The
  // icon it renders must NOT read this instance's own `theme` state — see
  // the staleness note on useIsDarkTheme in useTheme.ts.
  const { toggle: toggleTheme } = useTheme();
  const isDarkTheme = useIsDarkTheme();

  // Hydrate from the saved draft. Starting empty meant any fresh mount (e.g.
  // landing back here after confirming an email in another tab) raced ahead,
  // asked Alma for a new greeting, and overwrote the whole conversation.
  const [messages, setMessages] = useState<Message[]>(() => readPendingTryChat() ?? []);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [failed, setFailed] = useState(false);
  const [complete, setComplete] = useState(false);
  // The "I've got you" notice follows a beat after Alma's closing message, not with it. A finished chat restored from a draft shows it at once.
  const [closingShown, setClosingShown] = useState(false);
  const completedLiveRef = useRef(false);
  // Outcome of the finalize step, once attempted — drives which closing notice
  // the traveler sees. Null means "not attempted / in progress".
  const [outcome, setOutcome] = useState<FinishResult | null>(null);
  const [incompleteMessage, setIncompleteMessage] = useState<string | null>(null);
  const [finalizing, setFinalizing] = useState(false);
  const [resuming, setResuming] = useState(false);
  // Portrait extracted for review, and the modal's own submit state — kept
  // separate from `outcome`/`finalizing` since those describe the final
  // matching step, not the review step in between.
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [portraitDraft, setPortraitDraft] = useState<StructuredPortrait | null>(null);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [submittingPortrait, setSubmittingPortrait] = useState(false);
  const [portraitError, setPortraitError] = useState<string | null>(null);
  const [availableCities, setAvailableCities] = useState<string[]>([]);
  const { user, profile, loading: authLoading, activeRole, roles, switchRole } = useAuth();
  const [showAuth, setShowAuth] = useState(false);
  const [country, setCountry] = useState<string | null>(null);
  // On the native mobile app, unauthenticated users see the onboarding
  // carousel every time they open the app (not just once) — desktop web
  // keeps the one-time-ever behavior gated by hasSeenOnboarding.
  const [showIntro, setShowIntro] = useState(() => isNative || !hasSeenOnboarding());
  // The carousel is only ever shown to signed-out users, and only its own
  // dismiss clears `showIntro` — so for a signed-in user (always the case on
  // native, where showIntro starts true) anything gating on raw `showIntro`
  // would wait forever. Gate on whether the carousel is actually on screen.
  const introVisible = showIntro && !user && !authLoading;
  const [persona, setPersona] = useState<PersonaInfo | null>(null);
  const [showFaq, setShowFaq] = useState(false);
  const [showSupport, setShowSupport] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [personaReady, setPersonaReady] = useState(false);

  // Two-phase chat (flag alma_two_phase): tap questions first, then a short
  // conversation. Decided once per journey — a saved draft keeps the journey
  // it started on, and ?classic=1 forces the free-text chat for a new one.
  const twoPhaseFlag = useFeatureFlag('alma_two_phase');
  const [mode, setMode] = useState<'classic' | 'two-phase' | null>(() => {
    if (readFlowDraft()) return 'two-phase';
    if (readPendingTryChat() || searchParams.get('classic') === '1') return 'classic';
    return null;
  });
  useEffect(() => {
    if (mode === null && twoPhaseFlag !== undefined) setMode(twoPhaseFlag ? 'two-phase' : 'classic');
  }, [mode, twoPhaseFlag]);
  const isTwoPhase = mode === 'two-phase';

  const flow = useAlmaFlow({
    enabled: isTwoPhase && !introVisible && !authLoading && personaReady,
    persona: persona?.name ?? 'Alma',
    traveler: { signedIn: !!user, name: firstName(profile?.full_name) },
  });
  const flowState = flow.state;
  const p2Budget = usePart2Budget(isTwoPhase);
  useEffect(() => {
    if (!complete) {
      completedLiveRef.current = false;
      setClosingShown(false);
      return;
    }
    if (!completedLiveRef.current) {
      setClosingShown(true);
      return;
    }
    // The reply is still unfolding word by word after the stream ends: wait for that too.
    const last = [...messagesRef.current].reverse().find((m) => m.role === 'assistant');
    const unfolding = isTwoPhase && last ? revealDurationMs(last.content) : 0;
    const t = window.setTimeout(() => setClosingShown(true), unfolding + CLOSING_NOTICE_DELAY_MS);
    return () => window.clearTimeout(t);
  }, [complete, isTwoPhase]);
  // Read from inside stream()/sendMessage, which outlive a single render.
  const flowStateRef = useRef<FlowState | null>(flowState);
  flowStateRef.current = flowState;
  const flowDispatchRef = useRef(flow.dispatch);
  flowDispatchRef.current = flow.dispatch;

  /** Finished Phase 1 answers for alma-finalize — from the live flow, or the draft right after a sign-in redirect. */
  function currentAnswers(): AlmaFlowAnswers | null {
    if (mode !== 'two-phase') return null;
    const state = flowStateRef.current;
    return state ? flowAnswers(state) : readDraftAnswers();
  }

  /** Part 2 request extras, or undefined outside Part 2. */
  function phase2Request(): Phase2Request | undefined {
    const state = flowStateRef.current;
    if (!state || state.phase !== 2 || state.phase2From === null) return undefined;
    const answers = flowAnswers(state);
    if (!answers) return undefined;
    // Signed-in travelers weren't asked their name; Alma still gets it from the profile.
    const name = answers.traveler_name ?? (state.name || undefined);
    return { intake: { ...answers, ...(name ? { traveler_name: name } : {}) }, from: state.phase2From };
  }

  // Seeded from the same draft as `messages` — sendMessage reads this ref, so
  // leaving it empty would post a new turn without any of the prior context.
  const messagesRef = useRef<Message[]>(readPendingTryChat() ?? []);
  const bottomRef = useRef<HTMLDivElement>(null);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const busyRef = useRef(false);
  // Messages that arrived in this visit, as `role|timestamp`: only these play the arrival motion, never history or a restored draft.
  const liveRef = useRef(new Set<string>());
  // Later bubbles of a live reply that may appear (`role|timestamp|index`): each waits for the one before it to finish, plus a pause.
  const [readyBubbles, setReadyBubbles] = useState<Record<string, true>>({});
  const markLive = (m: { role: string; timestamp: string }) => { liveRef.current.add(`${m.role}|${m.timestamp}`); };
  const initialSentRef = useRef(false);
  const [scriptedTyping, setScriptedTyping] = useState(false);
  const drainingRef = useRef(false);

  function updateMessages(updater: (prev: Message[]) => Message[]) {
    setMessages(prev => {
      const next = updater(prev);
      messagesRef.current = next;
      // Guard against clobbering a longer saved transcript with a shorter one:
      // a destroyed draft silently becomes a 1-turn portrait downstream (wrong
      // trip length, placeholder trip name).
      const saved = readPendingTryChat();
      if (!saved || next.length >= saved.length) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      }
      return next;
    });
  }

  // The two-phase flow loads destinations with its other lists (below).
  useEffect(() => {
    if (mode !== 'classic') return;
    void supabase
      .from('destinations')
      .select('name')
      .eq('active', true)
      .order('sort_order')
      .then(({ data }) => setAvailableCities((data as { name: string }[] | null)?.map((d) => d.name) ?? []));
  }, [mode]);

  useEffect(() => {
    void detectCountry().then(setCountry);

    async function loadPersona() {
      const { data: p } = await supabase
        .from('ai_personas')
        .select('name, avatar_url')
        .eq('active', true)
        .order('created_at', { ascending: true })
        .limit(1)
        .single();
      if (p) setPersona(p);
      setPersonaReady(true);
    }
    void loadPersona();
  }, []);

  /** Streams a completion for `apiMessages` and appends/updates the trailing assistant bubble. */
  const stream = useCallback(async (apiMessages: Message[], phase2?: Phase2Request) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setFailed(false);
    setLoading(true);
    const requestStart = Date.now();

    // Pushed immediately, same as a real chat app's typing indicator — this
    // bubble IS the reply, showing dots (MessageBubble's `typing` prop, keyed
    // off empty content) until there's real text to swap in. One element,
    // one avatar, the whole turn: no separate indicator to mount/unmount/
    // crossfade against it, which is what read as a glitchy double-avatar
    // jump before.
    const assistantMsg: Message = { role: 'assistant', content: '', timestamp: new Date().toISOString() };
    markLive(assistantMsg);
    updateMessages(prev => [...prev, assistantMsg]);

    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
      const response = await fetch(`${supabaseUrl}/functions/v1/alma-conversation-public`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          apikey: import.meta.env.VITE_SUPABASE_ANON_KEY as string,
        },
        body: JSON.stringify({
          // Part 2: the model only sees Part 2, plus the Phase 1 answers.
          messages: (phase2 ? apiMessages.slice(phase2.from) : apiMessages).map(m => ({ role: m.role, content: m.content, ...(m.sig ? { sig: m.sig } : {}) })),
          country,
          ...(phase2 ? { intake: phase2.intake } : {}),
        }),
      });

      if (!response.ok || !response.body) throw new Error('Stream failed');

      // Holds the dots up for a minimum stretch so a fast/cached reply
      // doesn't flash past too quickly to register as "Alma is typing".
      const elapsed = Date.now() - requestStart;
      const remaining = MIN_TYPING_VISIBLE_MS - elapsed;
      if (remaining > 0) await new Promise(resolve => setTimeout(resolve, remaining));

      setLoading(false);
      setStreaming(true);

      let rawText = '';
      let sig: string | undefined;
      let hadError = false;
      let sawDone = false;

      for await (const payload of readSse(response.body)) {
        if (payload === '[DONE]') {
          sawDone = true;
          break;
        }
        try {
          const parsed = JSON.parse(payload);
          if (parsed.error) {
            hadError = true;
          } else if (parsed.text) {
            rawText += parsed.text;
          } else if (typeof parsed.sig === 'string') {
            sig = parsed.sig;
          }
          const visible = stripMarker(rawText);
          updateMessages(prev => {
            const updated = [...prev];
            updated[updated.length - 1] = { ...assistantMsg, content: visible };
            return updated;
          });
        } catch { /* skip malformed */ }
      }

      // A reply that errored, or ended without [DONE], is cut off: never keep half of it as Alma's answer.
      if (hadError || !sawDone) throw new Error('Stream cut off');

      const isComplete = rawText.includes(COMPLETE_MARKER);
      const visibleFinal = stripMarker(rawText);
      updateMessages(prev => {
        const updated = [...prev];
        updated[updated.length - 1] = { ...assistantMsg, content: visibleFinal, ...(sig ? { sig } : {}) };
        return updated;
      });

      setStreaming(false);
      // Set before the chat flips to complete, so the closing notice waits for the traveler to read the message.
      if (isComplete) completedLiveRef.current = true;
      if (phase2) {
        if (rawText.includes(ALMA_QUESTION_MARKER)) flowDispatchRef.current({ type: 'questionAsked' });
        if (isComplete) flowDispatchRef.current({ type: 'complete', now: Date.now() });
      }
      if (isComplete) {
        // Don't pop the auth modal over Alma's closing message — the traveler
        // should get to read it. A CTA below the thread opens it when ready.
        markTryChatComplete();
        setComplete(true);
      }
    } catch {
      // Drop the partial bubble (empty, or a half-written reply) and surface the retryable notice.
      // Retrying resends the history, so a truncated reply must not be left in it.
      updateMessages(prev => (prev[prev.length - 1]?.role === 'assistant' ? prev.slice(0, -1) : prev));
      setFailed(true);
    } finally {
      setLoading(false);
      setStreaming(false);
      busyRef.current = false;
    }
  }, [country]);

  const sendMessage = useCallback(async (content: string) => {
    if (busyRef.current) return;
    // Anything said after Alma wrapped up reopens the conversation — she'll
    // pick it up and wrap up again when ready.
    setComplete(false);
    clearTryChatComplete();
    const userMsg: Message = { role: 'user', content, timestamp: new Date().toISOString() };
    markLive(userMsg);
    // Snapshot BEFORE the state update — the ref only advances when React
    // runs the updater, which may be after stream() reads it.
    const apiMessages = [...messagesRef.current, userMsg];
    updateMessages(prev => [...prev, userMsg]);
    const phase2 = phase2Request();
    if (phase2) {
      trackAlma('alma_p2_question_answered', { index: apiMessages.slice(phase2.from).filter((m) => m.role === 'user').length });
    }
    await stream(apiMessages, phase2);
  }, [stream]);

  /** Re-runs the last request without appending anything new. */
  const retry = useCallback(() => {
    void stream([...messagesRef.current], phase2Request());
  }, [stream]);

  // Request a greeting from Alma
  useEffect(() => {
    // The two-phase flow opens with its own scripted lines.
    if (mode !== 'classic') return;
    if (initialSentRef.current || messages.length > 0 || introVisible || resuming) return;
    // Wait for auth: an authenticated traveler with a pending draft belongs in
    // the resume effect below, not in a brand-new conversation.
    if (authLoading) return;
    if (user && readPendingTryChat()) return;
    initialSentRef.current = true;
    void stream([]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, initialMessage, introVisible, resuming, authLoading, user]);

  // Google OAuth does a full-page redirect, which unmounts this component and
  // loses in-memory chat state before onSuccess can ever fire. The finished
  // draft survives in localStorage (see updateMessages below), so once we
  // land back here already authenticated, pick it up and finish the journey
  // instead of starting a brand-new, visibly blank chat.
  useEffect(() => {
    if (authLoading || !user || initialSentRef.current || mode === null) return;
    // Only a conversation Alma actually closed goes to review. The draft is
    // saved every turn, so an unfinished one (e.g. the app was reopened, or
    // /matches bounced back here mid-chat) just stays hydrated and continues.
    // A two-phase draft only goes to review once Alma has closed Part 2;
    // otherwise the flow itself resumes at the saved step.
    if (mode === 'two-phase' && !readFlowDraft()?.completedAt) return;
    const pending = readPendingTryChat();
    if (!pending || !isTryChatComplete()) return;
    initialSentRef.current = true;
    setResuming(true);
    setComplete(true);
    setMessages(pending);
    messagesRef.current = pending;
    void reviewTrip();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user, mode]);

  // ── Two-phase flow ──────────────────────────────────────────────────────

  // No cities to offer means nothing can be booked — use the classic chat.
  useEffect(() => {
    if (isTwoPhase && flow.unavailable && !flowState) {
      clearFlowDraft();
      setMode('classic');
    }
  }, [isTwoPhase, flow.unavailable, flowState]);

  useEffect(() => {
    if (flow.lists) setAvailableCities(flow.lists.cities.map((c) => c.name));
    for (const key of flow.lists?.failed ?? []) trackAlma('alma_option_list_failed', { question_key: key });
  }, [flow.lists]);

  // A finished Part 2 restored from the draft is still finished.
  useEffect(() => {
    if (flowState?.completedAt) setComplete(true);
  }, [flowState?.completedAt]);

  // Shows the flow's queued lines in order: the traveler's recap at once,
  // Alma's after a typing pause in the same bubble a streamed reply uses.
  useEffect(() => {
    const next = flowState?.outbox[0];
    if (!next || drainingRef.current) return;
    drainingRef.current = true;
    void (async () => {
      const timestamp = new Date().toISOString();
      // A reload mid-pause can leave the empty typing bubble behind — reuse it.
      const withoutTyping = (prev: Message[]) => {
        const last = prev[prev.length - 1];
        return last?.role === 'assistant' && last.content === '' ? prev.slice(0, -1) : prev;
      };
      if (next.role === 'assistant') {
        const msg: Message = { role: 'assistant', content: '', timestamp };
        markLive(msg);
        updateMessages((prev) => [...withoutTyping(prev), msg]);
        setScriptedTyping(true);
        await sleep(SCRIPTED_TYPING_MS);
        updateMessages((prev) => [...prev.slice(0, -1), { ...msg, content: next.text, flowId: next.id }]);
        setScriptedTyping(false);
      } else {
        markLive({ role: 'user', timestamp });
        updateMessages((prev) => [...withoutTyping(prev), { role: 'user', content: next.text, timestamp, flowId: next.id }]);
      }
      drainingRef.current = false;
      flow.dispatch({ type: 'ack', id: next.id });
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flowState?.outbox]);

  // Back: reopen the previous question and take its recap, and every line after it, out of the chat.
  // The lines fade out first, then the question slides back in from the left.
  const backTarget = flowState && !scriptedTyping ? rewindTarget(flowState) : null;
  const [rewindFrom, setRewindFrom] = useState<number | null>(null);
  const [cameBack, setCameBack] = useState(0);
  /** True only until the next question comes forward again. */
  const [backEnter, setBackEnter] = useState(false);
  const rewindTimerRef = useRef<number>();
  useEffect(() => () => window.clearTimeout(rewindTimerRef.current), []);
  function goBack() {
    if (!flowState || !backTarget || rewindFrom !== null) return;
    trackAlma('alma_p1_step_back', { from_step: flowState.step, to_step: backTarget.step });
    lightTap();
    const { fromMessageId } = backTarget;
    const rewind = () => {
      updateMessages((prev) => {
        const at = prev.findIndex((m) => m.flowId !== undefined && m.flowId >= fromMessageId);
        return at === -1 ? prev : prev.slice(0, at);
      });
      flow.dispatch({ type: 'goBack' });
      setCameBack((n) => n + 1);
      setBackEnter(true);
      setRewindFrom(null);
    };
    if (prefersReducedMotion()) return rewind();
    setRewindFrom(fromMessageId);
    rewindTimerRef.current = window.setTimeout(rewind, REWIND_FADE_MS);
  }

  const flowIdle = !!flowState && flowState.outbox.length === 0 && !scriptedTyping;
  // Moving forward queues lines, which hides the sheet; the next one rises as usual.
  useEffect(() => {
    if (!flowIdle) setBackEnter(false);
  }, [flowIdle]);
  const sheetVisible = isTwoPhase && flowIdle && flowState?.phase === 1 && !!flow.view
    && flow.view.step !== 'name' && flow.view.step !== 'handoff';
  // The last sheet shown, replayed with the leave motion at the hand-off.
  const lastSheetRef = useRef<SheetView | null>(null);
  if (sheetVisible && flow.view) lastSheetRef.current = flow.view;
  const atHandoff = !!flowState && flowState.step === 'handoff' && flowState.phase === 1;
  const [sheetGone, setSheetGone] = useState(false);
  // A question sheet is on screen (or just leaving): the tab bar below fades into the page behind it.
  const sheetShowing = (sheetVisible && !!flow.view) || (atHandoff && !sheetGone && !!lastSheetRef.current);

  useEffect(() => {
    if (!atHandoff) return;
    const t = window.setTimeout(() => setSheetGone(true), prefersReducedMotion() ? 0 : QUESTION_SHEET_MOTION_MS);
    return () => window.clearTimeout(t);
  }, [atHandoff]);

  // Hand-off: once the last recap is shown and the sheet has left, Alma
  // writes the first Part 2 message herself (a request with no new turn).
  useEffect(() => {
    if (!atHandoff || !sheetGone || !flowIdle || !flowState || busyRef.current) return;
    const now = Date.now();
    const from = messagesRef.current.length;
    flow.dispatch({ type: 'startPhase2', messageIndex: from, now });
    trackAlma('alma_p1_completed', { duration_ms: now - flowState.startedAt });
    trackAlma('alma_p2_started', {});
    const answers = flowAnswers({ ...flowState, phase: 2 });
    if (!answers) return;
    const name = answers.traveler_name ?? (flowState.name || undefined);
    void stream([...messagesRef.current], { intake: { ...answers, phase: 2, ...(name ? { traveler_name: name } : {}) }, from });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [atHandoff, sheetGone, flowIdle]);

  // Analytics: ids and counts only.
  const viewedStepRef = useRef<string | null>(null);
  useEffect(() => {
    if (!sheetVisible || !flow.view) return;
    // Each follow-up question is its own step: branch:<question_key>.
    const stepId = flow.view.step === 'branch' && flow.view.questionKey ? `branch:${flow.view.questionKey}` : flow.view.step;
    if (viewedStepRef.current !== stepId) {
      viewedStepRef.current = stepId;
      trackAlma('alma_p1_step_viewed', { step_id: stepId });
    }
  }, [sheetVisible, flow.view]);

  const lastStepRef = useRef<string | null>(null);
  useEffect(() => {
    const step = flowState?.step ? (flowState.step === 'branch' && flowState.branchKey ? `branch:${flowState.branchKey}` : flowState.step) : null;
    if (lastStepRef.current && step && step !== lastStepRef.current) {
      trackAlma('alma_p1_step_completed', { step_id: lastStepRef.current });
    }
    lastStepRef.current = step;
  }, [flowState?.step, flowState?.branchKey]);

  useEffect(() => {
    if (!flowState?.completedAt || !flowState.phase2StartedAt) return;
    trackAlma('alma_p2_completed', {
      questions_asked: flowState.p2Questions,
      duration_ms: flowState.completedAt - flowState.phase2StartedAt,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flowState?.completedAt]);

  useEffect(() => {
    if (!isTwoPhase) return;
    const onLeave = () => {
      const state = flowStateRef.current;
      if (state && !state.completedAt) trackAlma('alma_abandoned', { phase: state.phase, last_step: state.step });
    };
    window.addEventListener('pagehide', onLeave);
    return () => window.removeEventListener('pagehide', onLeave);
  }, [isTwoPhase]);

  // Also re-fires whenever a notice with an action button shows up
  // (finalizing/portrait-review/outcome), so that button is always in view
  // instead of requiring a manual scroll to find it. Two-phase: a question
  // sheet appearing shrinks the thread, so keep Alma's latest line in view.
  useEffect(() => {
    scrollListToBottom(bottomRef.current);
  }, [messages, readyBubbles, streaming, failed, complete, closingShown, finalizing, portraitDraft, showSummaryModal, outcome, sheetVisible, flowState?.step]);

  // Saves the conversation and asks Alma to extract a taste-map/trip-details
  // portrait for review, rather than finalizing straight away. Reusable so
  // the "try again" notice can re-run it, and so both the in-modal auth
  // success and the post-OAuth-redirect resume (above) share the same logic.
  async function reviewTrip() {
    setOutcome(null);
    setPortraitError(null);
    setFinalizing(true);
    const requestStart = Date.now();
    const holdMinimum = async () => {
      const remaining = MIN_FINALIZING_VISIBLE_MS - (Date.now() - requestStart);
      if (remaining > 0) await new Promise(resolve => setTimeout(resolve, remaining));
      setFinalizing(false);
    };

    const id = await ensureTryChatConversation(messagesRef.current);
    if (!id) {
      await holdMinimum();
      setOutcome('error');
      return;
    }
    setConversationId(id);
    const portrait = await extractTryChatPortrait(id, messagesRef.current, currentAnswers());
    await holdMinimum();
    if (!portrait) {
      setOutcome('error');
      return;
    }
    setPortraitDraft(portrait);
  }

  // Traveler confirmed (and possibly edited) the summary — this is what
  // actually persists the portrait, ranks hosts, and moves them on.
  async function submitTrip(edited: StructuredPortrait) {
    if (!conversationId) return;
    setSubmittingPortrait(true);
    setPortraitError(null);
    const result = await finishTryChatWithPortrait(conversationId, messagesRef.current, edited, currentAnswers());
    setSubmittingPortrait(false);
    if (result === 'matches') {
      // The host choice and payment are traveler pages: without this, someone
      // whose app was in host mode was bounced to the host board instead.
      if (activeRole !== 'traveler' && roles.includes('traveler')) await switchRole('traveler');
      navigate('/matches');
      return;
    }
    if (result === 'no-hosts') {
      setShowSummaryModal(false);
      setOutcome('no-hosts');
      return;
    }
    if (result === 'incomplete') {
      // The server won't map a taste from this chat yet. Close the review,
      // drop the draft portrait and reopen the conversation so the traveler
      // can tell Alma what's missing; she'll wrap up again when she has it.
      setShowSummaryModal(false);
      setPortraitDraft(null);
      setComplete(false);
      clearTryChatComplete();
      setIncompleteMessage(incompleteTripMessage(lastIncompleteReason()));
      setOutcome('incomplete');
      return;
    }
    setPortraitError("I couldn't quite find your host — want to try again?");
  }

  async function handleAuthSuccess() {
    setShowAuth(false);
    await reviewTrip();
  }

  function handleSend() {
    const text = input.trim();
    if (!text || failed) return;
    setInput('');
    // Telling Alma what she asked for resumes the conversation.
    if (outcome === 'incomplete') setOutcome(null);
    // Phase 1 name step: the typed name is the traveler's reply — no model call.
    if (isTwoPhase && flowState?.step === 'name') {
      updateMessages((prev) => [...prev, { role: 'user', content: text, timestamp: new Date().toISOString() }]);
      flow.dispatch({ type: 'submitName', name: text });
      return;
    }
    void sendMessage(text);
  }

  const personaName = persona?.name ?? 'Alma';
  // Once the taste map is being built (or shown), the conversation is over.
  // An 'incomplete' refusal sends the traveler back to the conversation.
  const mappingStarted = finalizing || portraitDraft !== null || (outcome !== null && outcome !== 'incomplete');
  const askingName = isTwoPhase && flowState?.step === 'name';
  // A signed-in traveler can keep talking after Alma wraps up (see the
  // "Map my taste" notice); a signed-out one has to sign in first.
  // Two-phase: the composer is only for the name and for Part 2.
  const inputDisabled = failed || mappingStarted || (complete && !user)
    || (isTwoPhase && !(askingName ? flowIdle : flowState?.phase === 2));

  // Alma has wrapped up and "Map my taste" is the next step: close the keyboard
  // and put focus on that button. Tapping the message box opens the keyboard
  // again to add something. Left alone if the traveler has started typing.
  const mapTasteShown = complete && closingShown && !!user && !mappingStarted && !streaming;
  useEffect(() => {
    if (!mapTasteShown || input.trim()) return;
    const active = document.activeElement;
    if (active instanceof HTMLTextAreaElement || active instanceof HTMLInputElement) active.blur();
    void hideKeyboard();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapTasteShown]);

  const almaAvatar = (size: number) => (
    <Avatar kind="alma" src={persona?.avatar_url ?? undefined} name={personaName} size={size} />
  );

  const signIn = (
    <button
      onClick={() => setShowAuth(true)}
      className="text-xs font-medium hover:underline underline-offset-2 sk-hit"
      style={{ color: 'var(--accent)' }}
    >
      Sign in
    </button>
  );

  // Two-phase header: progress takes the actions slot. Sign in moves next to
  // the subtitle on mobile; desktop already has it in the sidebar footer.
  const p1Progress = isTwoPhase && flowState?.phase === 1 ? flow.progress : null;
  const inPart2 = isTwoPhase && flowState?.phase === 2;
  // Never goes backwards; jumps to full when Alma closes early.
  const p2Value = complete ? p2Budget : Math.min(flowState?.p2Questions ?? 0, p2Budget);
  const headerProgress = p1Progress
    ? <FlowProgressCount current={p1Progress.current} total={p1Progress.total} ariaLabel={`Question ${p1Progress.current} of ${p1Progress.total}`} />
    : inPart2
      ? <FlowProgressCount prefix="Part " current={2} total={2} ariaLabel="Part 2 of 2" />
      : null;
  const mobileSignIn = isTwoPhase && !user ? (
    <span className="md:hidden inline-flex items-center gap-1.5"><span aria-hidden="true">·</span>{signIn}</span>
  ) : undefined;

  return (
    <div className="flex h-dvh bg-app-bg">
      {/* ── Journey sidebar (desktop) ── */}
      <div ref={sidebarRef} className="hidden md:flex shrink-0">
        <Sidebar
          style={{ height: '100%' }}
          headerActions={<IconButton icon={isDarkTheme ? 'sun' : 'moon'} label="Toggle theme" onClick={toggleTheme} />}
          footerItems={[
            { key: 'faq', icon: 'help-circle', label: 'FAQ', onClick: () => setShowFaq(true) },
            { key: 'support', icon: 'headset', label: 'Support', onClick: () => setShowSupport(true) },
            { key: 'feedback', icon: 'message-2', label: 'Feedback', onClick: () => setShowFeedback(v => !v) },
          ]}
          footerSlot={
            user ? (
              <button
                onClick={() => navigate('/home')}
                className="flex items-center gap-[9px] w-full text-left sk-hit"
              >
                <Avatar name={profile?.full_name ?? 'You'} src={profile?.avatar_url ?? undefined} size={30} />
                <span className="min-w-0">
                  <span className="block font-semibold text-footnote text-app-fg truncate">
                    {profile?.full_name ?? 'Signed in'}
                  </span>
                  <span className="block text-caption2 text-app-muted">Go to your account</span>
                </span>
              </button>
            ) : (
              <button
                onClick={() => setShowAuth(true)}
                className="flex items-center gap-[9px] w-full text-left sk-hit"
              >
                <span
                  className="w-[30px] h-[30px] rounded-full flex items-center justify-center shrink-0"
                  style={{ background: 'var(--surface)', border: '1px solid var(--line)' }}
                >
                  <i className="ti ti-user text-[length:calc(14px*var(--text-scale))] text-app-muted" />
                </span>
                <span className="min-w-0">
                  <span className="block font-semibold text-footnote text-app-fg">Sign in</span>
                  <span className="block text-caption2 text-app-muted">Save your conversation</span>
                </span>
              </button>
            )
          }
        >
          <div style={{ padding: '4px 4px 0' }}>
            <Stepper
              activeIndex={0}
              steps={[
                { label: 'Mapping your taste', sub: `${personaName}'s learning how you travel` },
                { label: 'Meet your hosts' },
                { label: 'Unlock your trip' },
              ]}
            />
          </div>
        </Sidebar>
      </div>

      {/* ── Chat column ── */}
      <div className="flex-1 min-w-0 flex flex-col h-full overflow-hidden">
        {/* Header — gradient fade below */}
        <div className="relative z-[2] shrink-0 w-full max-w-[720px] mx-auto pt-safe">
          <ChatHeader
            name={personaName}
            subtitle="Active now · AI assistant"
            avatar={almaAvatar(44)}
            style={{ padding: '16px 20px' }}
            actions={isTwoPhase ? headerProgress ?? undefined : user ? undefined : signIn}
            subtitleAction={mobileSignIn}
          />
          {p1Progress ? (
            <FlowProgressBar variant="segments" current={p1Progress.current} total={p1Progress.total} style={{ padding: '0 20px 10px' }} />
          ) : inPart2 ? (
            <FlowProgressBar variant="fill" value={p2Value} max={p2Budget} label={`${personaName}'s questions`} style={{ margin: '0 20px 10px' }} />
          ) : null}
          <div
            className="absolute left-0 right-0 top-full h-10 pointer-events-none z-[1]"
            style={{ background: 'linear-gradient(to bottom, var(--bg), transparent)' }}
          />
        </div>

        {/* Messages */}
        <div className="flex-1 min-h-0 overflow-y-auto flex flex-col" data-keyboard-pin>
          <div className="max-w-[720px] w-full mx-auto px-5 pt-6 pb-2 flex flex-col gap-4">
            {messages.flatMap((msg, i) => {
              const isUser = msg.role === 'user';
              const next = messages[i + 1];
              const lastInGroup = !next || next.role !== msg.role;
              // The trailing empty assistant bubble IS the typing indicator
              // (see stream() in TryChat) — same element the reply lands in,
              // just showing dots until there's text.
              const isPending = !isUser && i === messages.length - 1 && msg.content === '' && (loading || streaming || scriptedTyping);
              // Two-phase: Alma's paragraphs show as separate bubbles; the name goes on the first bubble of a group, the time on the last, and the avatar on the last one showing (it moves down as the next appears).
              const chunks = !isUser && isTwoPhase && !isPending ? splitBubbles(msg.content) : [msg.content];
              const firstInGroup = i === 0 || messages[i - 1].role !== msg.role;
              // Alma's words fade in one by one, the traveler's bubble rises in at once; only for what just arrived.
              const liveKey = `${msg.role}|${msg.timestamp}`;
              const reveal = isTwoPhase && liveRef.current.has(liveKey) ? (isUser ? 'bubble' : 'words') : undefined;
              const paced = reveal === 'words' && !prefersReducedMotion();
              // The avatar stays on the last bubble showing, so it moves down when the next one appears.
              const lastVisible = chunks.reduce((last, _, c) => (c === 0 || !paced || readyBubbles[`${liveKey}|${c}`] ? c : last), 0);
              return chunks.map((chunk, c) => {
                // A later bubble of a live reply waits for the one before it to be read out, and a pause, like a person texting twice.
                if (paced && c > 0 && !readyBubbles[`${liveKey}|${c}`]) return null;
                const lastChunk = c === chunks.length - 1;
                const showAvatar = lastInGroup && c === lastVisible;
                const nextKey = `${liveKey}|${c + 1}`;
                return (
                  <MessageBubble
                    key={`${i}-${msg.timestamp}-${c}`}
                    reveal={reveal}
                    onRevealed={paced && !lastChunk ? () => {
                      window.setTimeout(() => setReadyBubbles((prev) => (prev[nextKey] ? prev : { ...prev, [nextKey]: true })), BUBBLE_PAUSE_MS);
                    } : undefined}
                    from={isUser ? 'you' : 'other'}
                    typing={isPending}
                    time={lastChunk ? formatTime(msg.timestamp) : undefined}
                    senderName={!isUser && firstInGroup && c === 0 ? personaName : undefined}
                    avatar={!isUser && showAvatar ? almaAvatar(28) : undefined}
                    avatarMotion={paced && c > 0 ? 'drop' : undefined}
                    reserveGutter={!isUser && !showAvatar}
                    style={rewindFrom !== null && msg.flowId !== undefined && msg.flowId >= rewindFrom ? REWIND_FADE_STYLE : undefined}
                  >
                    {chunk}
                  </MessageBubble>
                );
              });
            })}

            {/* Conversation done, traveler not signed in yet — they read this,
                then choose when to continue. */}
            {complete && closingShown && !user && !finalizing && outcome === null && (
              <ChatNotice
                figure={illustration(barmanUrl, 108, 150)}
                headline="I've got you."
                message="Sign in to see your matches"
                actionLabel="Create Account"
                onAction={() => setShowAuth(true)}
              />
            )}

            {/* Conversation done, traveler signed in — confirm before mapping,
                and leave room to add anything else (the composer stays open). */}
            {complete && closingShown && user && !mappingStarted && !streaming && (
              <ChatNotice
                figure={illustration(barmanUrl, 108, 150)}
                headline="I've got you."
                message="Shall I map your taste now? Or tell me anything else first."
                actionLabel="Map my taste"
                onAction={() => void reviewTrip()}
                focusAction={!input.trim()}
              />
            )}

            {finalizing && (
              <ChatNotice
                figure={illustration(twoPeopleUrl, 120, 104)}
                headline="Mapping your taste..."
                headlineLoading
                headlineSize="sm"
              />
            )}

            {/* Portrait extracted — let them open the review before we go find a host. */}
            {!finalizing && portraitDraft && !showSummaryModal && outcome === null && (
              <ChatNotice
                figure={illustration(toastUrl, 120, 120)}
                headline="Your taste is mapped."
                message="Review the last details of your trip before finding the right host"
                actionLabel="Review your trip details"
                onAction={() => setShowSummaryModal(true)}
              />
            )}

            {outcome === 'unauthenticated' && (
              <ChatNotice
                figure={almaAvatar(56)}
                message="Almost — I still need you signed in. If you just registered, confirm your email first, then sign in."
                actionLabel="Sign in"
                onAction={() => setShowAuth(true)}
              />
            )}

            {/* Finalize worked; there simply are no bookable hosts yet.
                Retrying can't change that, so don't invite it. */}
            {outcome === 'no-hosts' && (
              <ChatNotice
                figure={almaAvatar(56)}
                message="I've saved everything you told me — but I don't have a host available for your trip just yet. I'll have someone for you soon."
              />
            )}

            {/* alma-finalize refused: not enough to map yet. The composer is
                open again — whatever they type next goes to Alma. */}
            {outcome === 'incomplete' && incompleteMessage && (
              <ChatNotice figure={almaAvatar(56)} message={incompleteMessage} />
            )}

            {outcome === 'error' && (
              <ChatNotice
                figure={almaAvatar(56)}
                message="I couldn't quite finish finding your hosts. Want me to try again?"
                actionLabel="Try again"
                onAction={() => void reviewTrip()}
              />
            )}

            {failed && (
              <ChatNotice
                message="Give me a second — something went quiet on my end. Want to try again?"
                actionLabel="Try again"
                onAction={retry}
              />
            )}

            <div ref={bottomRef} className="h-2 shrink-0" />
          </div>
        </div>

        {/* Two-phase Phase 1: the question sheet replaces the composer. */}
        {sheetVisible && flow.view ? (
          <div className="shrink-0 max-w-[720px] w-full mx-auto">
            <AlmaFlowSheet
              // A new key per back replays the slide-in, even though the sheet itself never unmounted.
              key={cameBack}
              view={flow.view}
              dispatch={flow.dispatch}
              today={flow.today}
              onRetry={() => void flow.retry()}
              onBack={backTarget ? goBack : undefined}
              enter={backEnter ? 'back' : 'up'}
              docked={!!user}
            />
          </div>
        ) : atHandoff && !sheetGone && lastSheetRef.current ? (
          <div className="shrink-0 max-w-[720px] w-full mx-auto">
            <AlmaFlowSheet view={lastSheetRef.current} dispatch={() => {}} today={flow.today} onRetry={() => {}} docked={!!user} leaving />
          </div>
        ) : isTwoPhase && flowState?.phase === 1 && !askingName ? (
          // Between questions (Alma's line is on its way): hold the space quietly.
          null
        ) : (
          /* Composer + disclaimer */
          /* When signed in on mobile, the floating tab bar below owns the
             safe-area clearance (same split as TripChat's composer). */
          <div className={`shrink-0 max-w-[720px] w-full mx-auto px-4 pt-3 ${user ? 'pb-2.5 md:pb-5 md:pb-safe-or-5' : 'pb-5 pb-safe-or-5'}`}>
            <ChatComposer
              value={input}
              onChange={setInput}
              onSend={handleSend}
              placeholder={askingName ? COMPOSER_PLACEHOLDER_NAME : `Message ${personaName}…`}
              disabled={inputDisabled}
              disclaimer="Never share passport numbers, banking details, or home address in chat."
              maxLength={ALMA_LIMITS.maxUserMessageChars}
            />
          </div>
        )}

        {user && <TravelerTabBar active="chat" fade={sheetShowing} />}
      </div>

      {/* Onboarding carousel — first thing an anonymous user sees. It sits
          directly over the live chat page; the carousel's own scrim blurs it. */}
      {introVisible && <OnboardingCarousel country={country} onDismiss={() => setShowIntro(false)} />}

      {/* Trip summary review — opened from the "Review your trip details" notice above */}
      {showSummaryModal && portraitDraft && (
        <TripSummaryModal
          portrait={portraitDraft}
          submitting={submittingPortrait}
          error={portraitError}
          onClose={() => setShowSummaryModal(false)}
          onSubmit={(edited) => { setPortraitDraft(edited); void submitTrip(edited); }}
          availableCities={availableCities}
          walkingOptions={flow.lists?.walking.map((o) => ({ slug: o.slug, label: walkingLabel(o, flowState?.group ?? null) }))}
        />
      )}

      {/* Auth modal */}
      {showAuth && (
        <AuthModal
          onSuccess={() => void handleAuthSuccess()}
          // Always closable — the traveler may want to re-read Alma's closing
          // message. The CTA in the thread brings this back.
          onClose={() => setShowAuth(false)}
          isRequired={complete}
        />
      )}

      {showFaq && <FaqModal audience="traveler" onClose={() => setShowFaq(false)} />}
      {showSupport && <SupportModal onClose={() => setShowSupport(false)} />}
      {showFeedback && <FeedbackPopover anchorRef={sidebarRef} onClose={() => setShowFeedback(false)} />}
    </div>
  );
}

function AuthModal({ onSuccess, onClose, isRequired }: { onSuccess: () => void; onClose?: () => void; isRequired: boolean }) {
  const [mode, setMode] = useState<'login' | 'register'>('register');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [error, setError] = useState('');
  const [confirmSent, setConfirmSent] = useState(false);
  const [loading, setLoading] = useState(false);

  // Android hardware back button closes this modal instead of navigating
  // the route underneath it — only when a close affordance exists.
  useEffect(() => {
    if (!onClose) return;
    return registerModalBackHandler(onClose);
  }, [onClose]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    const problem = mode === 'register' ? passwordProblem(password) : null;
    if (problem) {
      setError(problem);
      return;
    }
    setLoading(true);
    try {
      if (mode === 'register') {
        const { data, error: signUpError } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: { full_name: fullName },
            // Land on the route that actually completes the auth exchange —
            // without this Supabase falls back to the project Site URL (the
            // app root), which drops the code and silently does nothing. On
            // native, `window.location.origin` is the webview's internal
            // origin (e.g. https://localhost), which means nothing to the
            // Mail app that opens this link — so use the same custom-scheme
            // deep link OAuth already relies on, and let the appUrlOpen
            // handler in lib/native.ts complete the exchange when the app is
            // opened from it.
            emailRedirectTo: isNative ? OAUTH_REDIRECT_NATIVE : OAUTH_REDIRECT_WEB,
          },
        });
        if (signUpError) throw signUpError;
        // With email confirmation on, signUp returns a user but NO session —
        // the traveler isn't signed in yet, so finishing the journey now would
        // fail. Tell them to confirm instead of pretending it worked.
        if (!data.session) {
          setConfirmSent(true);
          return;
        }
      } else {
        const { error: signInError } = await supabase.auth.signInWithPassword({ email, password });
        if (signInError) throw signInError;
      }
      onSuccess();
    } catch (err: unknown) {
      setError((mode === 'register' ? friendlyPasswordError(err) : null) ?? (err instanceof Error ? err.message : 'Authentication failed'));
    } finally {
      setLoading(false);
    }
  }

  if (confirmSent) {
    return (
      <div className="fixed inset-0 bg-black/60 z-50 flex items-end md:items-center justify-center" onClick={onClose}>
        <div className="bg-app-card md:border md:border-app-border rounded-t-3xl md:rounded-2xl w-full max-w-md pb-safe-or-6 px-6 py-8 text-center" onClick={(e) => e.stopPropagation()}>
          <h2 className="font-serif text-xl text-app-fg mb-2">Check your email</h2>
          <p className="text-sm text-app-muted leading-relaxed mb-6">
            We sent a confirmation link to <span className="text-app-fg">{email}</span>. Open it to
            activate your account, then come back and sign in — your conversation is saved.
          </p>
          <Button
            size="lg"
            className="w-full"
            onClick={() => { setConfirmSent(false); setMode('login'); setPassword(''); }}
          >
            I've confirmed — sign in
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-end md:items-center justify-center" onClick={onClose}>
      <div className="bg-app-card md:border md:border-app-border rounded-t-3xl md:rounded-2xl w-full max-w-md pb-safe-or-6 max-h-dvh-90 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 pt-6 pb-4 border-b border-app-border flex items-center justify-between">
          <div>
            <h2 className="font-serif text-xl text-app-fg">
              {isRequired ? 'One last step' : 'Sign in to Sekretly'}
            </h2>
            {isRequired && (
              <p className="text-sm text-app-muted mt-1">Create an account to continue with your matches.</p>
            )}
          </div>
          {onClose && (
            <button onClick={onClose} className="w-8 h-8 rounded-full bg-app-bg border border-app-border text-app-muted flex items-center justify-center hover:text-app-fg shrink-0 sk-hit">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
              </svg>
            </button>
          )}
        </div>

        <form onSubmit={handleSubmit} className="px-6 pt-5 flex flex-col gap-4">
          {mode === 'register' && (
            <Input label="Full name" value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="Your name" required autoComplete="name" />
          )}
          <Input label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" required autoComplete="email" />
          <Input label="Password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required autoComplete={mode === 'register' ? 'new-password' : 'current-password'} aria-describedby={mode === 'register' ? 'password-rules' : undefined} />
          {mode === 'register' && <PasswordRules password={password} />}
          {error && <p className="text-sm text-crimson">{error}</p>}
          <Button type="submit" size="lg" disabled={loading} className="w-full">
            {loading ? <span className="flex items-center gap-2"><Spinner size="sm" className="text-ink" />Please wait…</span>
              : mode === 'register' ? 'Create account' : 'Sign in'}
          </Button>
        </form>

        <div className="px-6 mt-4">
          <OAuthButtons />
        </div>

        <p className="text-center text-sm text-app-muted px-6 mt-5 pb-2">
          {mode === 'register' ? (
            <>Already have an account?{' '}<button onClick={() => { setMode('login'); setError(''); }} className="text-gold font-medium underline underline-offset-2 sk-hit">Sign in</button></>
          ) : (
            <>Don't have an account?{' '}<button onClick={() => { setMode('register'); setError(''); }} className="text-gold font-medium underline underline-offset-2 sk-hit">Register</button></>
          )}
        </p>
      </div>
    </div>
  );
}

export default TryChat;
